import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, ArrowLeft, Check } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Sprint 60.30 — Reset Password page. Reads token from ?token= query
 * param, POSTs {token, new_password} to /api/auth/reset-password.
 * On success, redirects to /login with a success toast.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token. Please request a new link.");
    }
  }, [token]);

  const canSubmit =
    token && pw.length >= 8 && pw === confirm && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. Redirecting to login…");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error("Too many attempts. Try again in an hour.");
      } else {
        toast.error(detail || "Reset failed. Please request a new link.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md border border-zinc-800 bg-zinc-950/80 backdrop-blur p-8">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-widest text-zinc-500 hover:text-[#39FF14] mb-6"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={12} /> Back to login
        </Link>
        <h1 className="text-2xl font-bold mb-1">Choose a new password</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Minimum 8 characters. This link is single-use and expires 60 minutes after it was sent.
        </p>

        {done ? (
          <div
            data-testid="reset-done-confirmation"
            className="border border-[#39FF14]/50 bg-[#39FF14]/10 p-4 flex items-center gap-3"
          >
            <Check size={24} weight="bold" className="text-[#39FF14]" />
            <div>
              <div className="text-[11px] mono uppercase tracking-widest text-[#39FF14]">
                Password updated
              </div>
              <p className="text-sm text-zinc-300">Redirecting to login…</p>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="reset-password-form" className="space-y-4">
            <div>
              <label className="text-[11px] mono uppercase tracking-widest text-zinc-500 mb-1.5 block">
                New password
              </label>
              <div className="relative">
                <LockKey
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Minimum 8 characters"
                  data-testid="reset-new-password-input"
                  className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none pl-10 pr-3 py-2.5 text-sm text-zinc-100"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] mono uppercase tracking-widest text-zinc-500 mb-1.5 block">
                Confirm password
              </label>
              <div className="relative">
                <LockKey
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retype your new password"
                  data-testid="reset-confirm-password-input"
                  className={`w-full bg-black border outline-none pl-10 pr-3 py-2.5 text-sm text-zinc-100 ${
                    confirm.length > 0 && confirm !== pw
                      ? "border-red-500/60 focus:border-red-500"
                      : "border-zinc-800 focus:border-[#39FF14]"
                  }`}
                />
              </div>
              {confirm.length > 0 && confirm !== pw && (
                <p className="text-[10px] text-red-400 mt-1">Passwords do not match.</p>
              )}
            </div>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="reset-submit-btn"
              className="w-full bg-[#39FF14] hover:bg-[#2fd811] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold py-3 uppercase tracking-widest text-xs transition-colors"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
